/**
 * First-run onboarding (shown by App while me.mustOnboard): name → photo → phone/e-mail
 * sharing consent → language. Everything except the photo (saved immediately by the
 * AvatarEditor) is sent in one PATCH /api/profile with mustOnboard:false at the end.
 * Rendered outside the router.
 */
import { ArrowLeft, ArrowRight, Check, Globe, LogOut, Phone, UserRound, Camera, type LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { ProfileUpdateRequest } from '../../../shared/dto/members';
import { logout, useUpdateProfile } from '../../api/auth';
import { ApiFailure } from '../../api/client';
import { useRequiredMe } from '../../auth/AuthProvider';
import { Wordmark } from '../../components/brand';
import { useToast } from '../../components/toast';
import { Button, Card, ErrorBox, cx } from '../../components/ui';
import { useI18n, type MessageKey } from '../../i18n';
import { AuthLayout } from './AuthLayout';
import { apiErrorText } from './errors';
import { ContactStep, LangStep, NameStep, PhotoStep, type ContactInvalid, type ContactState } from './OnboardingSteps';
import { emailOk, phoneOk, rejectedFields } from './validation';

type StepId = 'name' | 'photo' | 'contact' | 'lang';
const STEPS: { id: StepId; icon: LucideIcon; title: MessageKey; body: MessageKey }[] = [
  { id: 'name', icon: UserRound, title: 'auth.onb.name.title', body: 'auth.onb.name.body' },
  { id: 'photo', icon: Camera, title: 'auth.onb.photo.title', body: 'auth.onb.photo.body' },
  { id: 'contact', icon: Phone, title: 'auth.onb.contact.title', body: 'auth.onb.contact.body' },
  { id: 'lang', icon: Globe, title: 'auth.onb.lang.title', body: 'auth.onb.lang.body' },
];
const CONTACT_STEP = STEPS.findIndex((s) => s.id === 'contact');

function ProgressDots({ step }: { step: number }) {
  const { t } = useI18n();
  return (
    <div className="flex flex-col items-center gap-2">
      <ol className="flex items-center gap-2" aria-hidden>
        {STEPS.map((s, i) => (
          <li
            key={s.id}
            className={cx(
              'h-3 rounded-full transition-all duration-200',
              i === step ? 'w-9 bg-[var(--accent)]' : i < step ? 'w-3 bg-[var(--accent)]/60' : 'w-3 bg-[var(--line)]',
            )}
          />
        ))}
      </ol>
      <p className="text-[15px] font-semibold text-[var(--muted)]" aria-live="polite">
        {t('auth.onb.step_of', { n: step + 1, total: STEPS.length })}
      </p>
    </div>
  );
}

export default function OnboardingScreen() {
  const me = useRequiredMe();
  const { t, lang } = useI18n();
  const toast = useToast();
  const update = useUpdateProfile();
  const [step, setStep] = useState(0);
  const [tried, setTried] = useState(false);
  const [fullName, setFullName] = useState(me.fullName);
  const [contact, setContact] = useState<ContactState>({
    phone: me.phone ?? '',
    sharePhone: me.sharePhone,
    shareEmail: me.shareEmail,
    emailPublic: me.emailPublic ?? '',
  });
  /** Fields the server rejected on finish; cleared as soon as the member edits that field. */
  const [serverInvalid, setServerInvalid] = useState<ContactInvalid>({});
  const [signingOut, setSigningOut] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    headingRef.current?.focus({ preventScroll: false });
  }, [step]);

  const current = STEPS[step]!;
  const last = step === STEPS.length - 1;

  const stepValid = (id: StepId): boolean => {
    if (id === 'name') return fullName.trim().length > 0;
    if (id === 'contact') {
      return (
        phoneOk(contact.phone) &&
        (!contact.shareEmail || emailOk(contact.emailPublic)) &&
        !serverInvalid.phone &&
        !serverInvalid.emailPublic
      );
    }
    return true;
  };

  const changeContact = (v: ContactState) => {
    if (serverInvalid.phone && v.phone !== contact.phone) setServerInvalid((s) => ({ ...s, phone: false }));
    if (serverInvalid.emailPublic && (v.emailPublic !== contact.emailPublic || v.shareEmail !== contact.shareEmail)) {
      setServerInvalid((s) => ({ ...s, emailPublic: false }));
    }
    setContact(v);
  };

  const finish = async () => {
    const phone = contact.phone.trim();
    const emailPublic = contact.emailPublic.trim();
    const patch: ProfileUpdateRequest = {
      fullName: fullName.trim(),
      phone: phone || null,
      sharePhone: phone ? contact.sharePhone : false,
      shareEmail: contact.shareEmail,
      emailPublic: contact.shareEmail && emailPublic ? emailPublic : null,
      lang,
      mustOnboard: false,
    };
    try {
      await update.mutateAsync(patch);
      toast.show(t('auth.onb.done_toast'), { tone: 'ok' });
      // ['me'] now has mustOnboard=false → App switches to the app.
    } catch (err) {
      // A rejected phone / public e-mail: go back to that step and show the error at the field.
      // Anything else is shown from update.error below.
      if (!(err instanceof ApiFailure && err.code === 'validation')) return;
      const fields = rejectedFields(err.details);
      const bad: ContactInvalid = { phone: fields.has('phone'), emailPublic: fields.has('emailPublic') };
      if (!bad.phone && !bad.emailPublic) return;
      update.reset();
      setServerInvalid(bad);
      setTried(true);
      setStep(CONTACT_STEP);
    }
  };

  const next = (e?: FormEvent) => {
    e?.preventDefault();
    if (update.isPending) return;
    if (!stepValid(current.id)) {
      setTried(true);
      return;
    }
    setTried(false);
    if (last) void finish();
    else setStep(step + 1);
  };

  const back = () => {
    setTried(false);
    update.reset();
    setStep(Math.max(0, step - 1));
  };

  const signOut = async () => {
    setSigningOut(true);
    try {
      await logout();
    } catch (err) {
      toast.show(apiErrorText(t, err), { tone: 'error' });
      setSigningOut(false);
    }
  };

  const skipPhoto = current.id === 'photo' && me.avatarRev === 0;

  return (
    <AuthLayout
      wide
      topLeft={
        <Button variant="ghost" size="sm" icon={LogOut} busy={signingOut} onClick={() => void signOut()} className="min-h-11">
          {t('auth.onb.sign_out')}
        </Button>
      }
    >
      <div className="mb-5 flex flex-col items-center gap-3 text-center">
        <Wordmark />
        {step === 0 ? (
          <>
            <h1 className="mt-2 text-[28px] leading-tight font-bold tracking-tight">{t('auth.onb.welcome')}</h1>
            <p className="text-[18px] text-[var(--ink-2)]">{t('auth.onb.intro')}</p>
          </>
        ) : null}
      </div>

      <Card className="px-5 py-6 md:px-8 md:py-8">
        <ProgressDots step={step} />

        <form onSubmit={next} noValidate className="mt-6 flex flex-col gap-6">
          <div className="flex items-start gap-4">
            <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-[var(--accent-soft)] text-[var(--accent)]">
              <current.icon className="size-6" aria-hidden />
            </span>
            <div className="min-w-0">
              <h2 ref={headingRef} tabIndex={-1} className="text-[24px] leading-tight font-bold tracking-tight outline-none">
                {t(current.title)}
              </h2>
              <p className="mt-1 text-[17px] text-[var(--ink-2)]">{t(current.body)}</p>
            </div>
          </div>

          <div>
            {current.id === 'name' ? <NameStep value={fullName} onChange={setFullName} showError={tried} /> : null}
            {current.id === 'photo' ? <PhotoStep me={me} /> : null}
            {current.id === 'contact' ? <ContactStep me={me} value={contact} onChange={changeContact} showErrors={tried} serverInvalid={serverInvalid} /> : null}
            {current.id === 'lang' ? <LangStep /> : null}
          </div>

          {update.isError ? <ErrorBox>{apiErrorText(t, update.error)}</ErrorBox> : null}

          <div className="flex flex-col-reverse gap-3 border-t border-[var(--line)] pt-5 sm:flex-row sm:items-center">
            {step > 0 ? (
              <Button variant="secondary" size="lg" icon={ArrowLeft} onClick={back} disabled={update.isPending} className="sm:min-w-40">
                {t('common.back')}
              </Button>
            ) : null}
            <div className="hidden flex-1 sm:block" />
            <Button
              type="submit"
              size="lg"
              variant={skipPhoto ? 'secondary' : 'primary'}
              icon={last ? Check : undefined}
              iconRight={last ? undefined : ArrowRight}
              busy={update.isPending}
              className="sm:min-w-48"
            >
              {last ? t('auth.onb.finish') : skipPhoto ? t('auth.onb.skip') : t('auth.onb.next')}
            </Button>
          </div>
        </form>
      </Card>
    </AuthLayout>
  );
}
